import { useState, useEffect, useCallback, useRef } from 'react'
import { SupabaseClient } from '@supabase/supabase-js'
import { WebbMetrics } from './types'

const DEFAULT_METRICS: WebbMetrics = {
  totalUsers: 0,
  paidUsers: 0,
  totalMessagesSent: 0,
  avgMessagesPerUser: 0,
  avgMessagesPerPaidUser: 0,
  avgMessagesPerFreeUser: 0,
  totalCanvases: 0,
  totalProjects: 0,
  totalDocuments: 0,
  planBreakdown: {},
  tierBreakdown: {},
  variantBreakdown: {},
  monthlyRevenue: {},
  activeSubscriptions: 0,
  canceledSubscriptions: 0,
  newUsersLast24h: 0,
  newUsersLast7d: 0,
}

const STORAGE_KEY = 'webb_metrics_cache'
const LOCAL_CACHE_TTL_MS = 10 * 60 * 1000 // 10 minutes

interface CachedMetrics {
  metrics: WebbMetrics
  timestamp: number
}

function getLocalCache(): CachedMetrics | null {
  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (cached) {
      const parsed = JSON.parse(cached) as CachedMetrics
      if (Date.now() - parsed.timestamp < LOCAL_CACHE_TTL_MS) {
        return parsed
      }
    }
  } catch (e) {
    console.warn('Failed to read Webb metrics cache:', e)
  }
  return null
}

function setLocalCache(metrics: WebbMetrics): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ metrics, timestamp: Date.now() }))
  } catch (e) {
    console.warn('Failed to write Webb metrics cache:', e)
  }
}

export function useWebbMetrics(client: SupabaseClient | null) {
  const [metrics, setMetrics] = useState<WebbMetrics>(() => {
    const cached = getLocalCache()
    return cached?.metrics || DEFAULT_METRICS
  })
  const [isLoading, setIsLoading] = useState(() => !getLocalCache())
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isMounted = useRef(true)

  const fetchMetrics = useCallback(async (forceRefresh = false) => {
    if (!client) {
      setIsLoading(false)
      return
    }

    if (!forceRefresh) {
      const cached = getLocalCache()
      if (cached?.metrics) {
        setMetrics(cached.metrics)
        setIsLoading(false)
        const cacheAge = Date.now() - cached.timestamp
        if (cacheAge < LOCAL_CACHE_TTL_MS) {
          console.log(`WebbMetrics: Using cached data (${Math.round(cacheAge / 1000)}s old)`)
          return
        }
        console.log('WebbMetrics: Cache stale, refreshing in background...')
      }
    }

    const hasData = metrics.totalUsers > 0
    if (hasData) {
      setIsRefreshing(true)
    } else {
      setIsLoading(true)
    }
    setError(null)

    try {
      const { data, error: rpcError } = await client.rpc('get_admin_stats')

      if (rpcError) throw new Error(`RPC failed: ${rpcError.message}`)

      console.log('WebbMetrics: got stats', data)

      const stats = data as Record<string, any>

      const newMetrics: WebbMetrics = {
        totalUsers: stats.totalUsers ?? 0,
        paidUsers: stats.paidUsers ?? 0,
        totalMessagesSent: stats.totalMessagesSent ?? 0,
        avgMessagesPerUser: stats.avgMessagesPerUser ?? 0,
        avgMessagesPerPaidUser: stats.avgMessagesPerPaidUser ?? 0,
        avgMessagesPerFreeUser: stats.avgMessagesPerFreeUser ?? 0,
        totalCanvases: stats.totalCanvases ?? 0,
        totalProjects: stats.totalProjects ?? 0,
        totalDocuments: stats.totalDocuments ?? 0,
        planBreakdown: stats.planBreakdown ?? {},
        tierBreakdown: stats.tierBreakdown ?? {},
        variantBreakdown: stats.variantBreakdown ?? {},
        monthlyRevenue: stats.monthlyRevenue ?? {},
        activeSubscriptions: stats.activeSubscriptions ?? 0,
        canceledSubscriptions: stats.canceledSubscriptions ?? 0,
        newUsersLast24h: stats.newUsersLast24h ?? 0,
        newUsersLast7d: stats.newUsersLast7d ?? 0,
      }

      if (isMounted.current) {
        setMetrics(newMetrics)
        setLocalCache(newMetrics)
      }
    } catch (err: any) {
      console.error('Error fetching Webb metrics:', err)
      if (isMounted.current && !hasData) {
        setError(err.message)
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    }
  }, [client, metrics])

  useEffect(() => {
    isMounted.current = true
    fetchMetrics()
    return () => { isMounted.current = false }
  }, [client])

  return { metrics, isLoading, isRefreshing, error, refetch: fetchMetrics }
}
